/**
 * Shared transaction builders. Per FR-S06 — both the relay path
 * (`pipeline.ts`) and the Turnkey-sponsored path (`turnkey/submit.ts`)
 * must compose the final `VersionedTransaction` from the same helper
 * so compute-budget injection and Jito-tip wiring
 * can never drift between the two paths.
 * This file only assembles transactions; confirmation stays with the
 * submission pipeline, Turnkey activity polling, and indexed/NATS trade watch
 * modules.
 *
 * Relocated from frontend/src/lib/tx-engine/build-tx.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E). Imports only @solana/web3.js
 * (universal) — platform-neutral, safe in src/auth/ core.
 */

import {
  ComputeBudgetProgram,
  MessageV0,
  PublicKey,
  type TransactionInstruction,
  VersionedTransaction,
} from '@solana/web3.js';
import { Buffer } from 'buffer';
import type { RecentBlockhash } from '../../blockchain/blockhash/index.js';
import { createJitoTipInstruction, isJitoAvailable } from '../submit/jito.js';
import type { SubmissionOptions } from '../types.js';

const DEFAULT_COMPUTE_UNITS = 200_000;
const MAX_COMPUTE_UNITS = 1_400_000;

/** Solana's hard per-packet transaction size limit. */
export const MAX_PACKET_BYTES = 1232;

/**
 * Bytes the Turnkey sponsor rewrite adds to a tx serialized with the USER as
 * fee payer: the sponsor pubkey enters the static account keys (+32) and a
 * second required signature is added (+64). Observed empirically: a 1160-byte
 * locally-serialized swap became 1253 bytes after Turnkey's sponsor + rent-
 * discovery wrapping (+93); 96 is the exact 32+64 ceiling, so this never under-
 * counts the real overhead. (The relay path needs no addend — its serialized tx
 * already carries the relay fee payer + signature slot.)
 */
export const SPONSOR_SIZE_OVERHEAD = 96;

/**
 * Whether a locally-serialized tx will exceed {@link MAX_PACKET_BYTES} once its
 * fee payer is attached. `'sponsor'` (Turnkey) adds the sponsor server-side, so
 * the overhead is projected; `'relay'` already carries its fee payer, so the
 * serialized length is final. Lets both submit paths reject an oversized tx
 * BEFORE the slow network round-trip (Turnkey otherwise only reports "too large"
 * ~30s later via the signature poll).
 */
export function exceedsPacketLimit(
  localSerializedBytes: number,
  feePayer: 'sponsor' | 'relay',
): boolean {
  const projected =
    feePayer === 'sponsor' ? localSerializedBytes + SPONSOR_SIZE_OVERHEAD : localSerializedBytes;
  return projected > MAX_PACKET_BYTES;
}

export interface NormalizedTransactionPacketMeasurement {
  localSerializedBytes: number;
  projectedSerializedBytes: number;
  maximumPacketBytes: number;
  fits: boolean;
}

export interface MeasureNormalizedTransactionPacketArgs {
  buildResult: BuildResult;
  payer: PublicKey;
  feePayer: 'sponsor' | 'relay';
}

/**
 * Measures the same normalized v0 transaction produced by the submission
 * paths, including compute-budget replacement and sponsor projection.
 */
export function measureNormalizedTransactionPacket(
  args: MeasureNormalizedTransactionPacketArgs,
): NormalizedTransactionPacketMeasurement {
  const transaction = buildFinalTransaction({
    buildResult: args.buildResult,
    priorityFee: 0,
    blockhash: { blockhash: '11111111111111111111111111111111' },
    payer: args.payer,
  });
  const localSerializedBytes = measureVersionedTransactionBytes(transaction);
  const projectedSerializedBytes =
    args.feePayer === 'sponsor'
      ? localSerializedBytes + SPONSOR_SIZE_OVERHEAD
      : localSerializedBytes;

  return {
    localSerializedBytes,
    projectedSerializedBytes,
    maximumPacketBytes: MAX_PACKET_BYTES,
    fits: projectedSerializedBytes <= MAX_PACKET_BYTES,
  };
}

function shortVecBytes(value: number): number {
  let remaining = value;
  let bytes = 0;
  do {
    bytes += 1;
    remaining = Math.floor(remaining / 128);
  } while (remaining > 0);
  return bytes;
}

function measureVersionedTransactionBytes(transaction: VersionedTransaction): number {
  const message = transaction.message;
  const signaturesBytes =
    shortVecBytes(transaction.signatures.length) + transaction.signatures.length * 64;
  const staticKeysBytes =
    shortVecBytes(message.staticAccountKeys.length) + message.staticAccountKeys.length * 32;
  const instructionBytes = message.compiledInstructions.reduce(
    (total, instruction) =>
      total +
      1 +
      shortVecBytes(instruction.accountKeyIndexes.length) +
      instruction.accountKeyIndexes.length +
      shortVecBytes(instruction.data.length) +
      instruction.data.length,
    shortVecBytes(message.compiledInstructions.length),
  );
  const lookupBytes = message.addressTableLookups.reduce(
    (total, lookup) =>
      total +
      32 +
      shortVecBytes(lookup.writableIndexes.length) +
      lookup.writableIndexes.length +
      shortVecBytes(lookup.readonlyIndexes.length) +
      lookup.readonlyIndexes.length,
    shortVecBytes(message.addressTableLookups.length),
  );
  const messageBytes =
    1 + // v0 version prefix
    3 + // message header
    staticKeysBytes +
    32 + // recent blockhash
    instructionBytes +
    lookupBytes;
  return signaturesBytes + messageBytes;
}

// Monotonic nonce shared across both paths so concurrent transactions
// never collide on the Solana dedup hash. Wraps at 256 — even a 1-unit
// difference in compute-limit changes the tx bytes.
let txNonce = 0;
function nextNonce(): number {
  txNonce = (txNonce + 1) % 256;
  return txNonce;
}

function createComputeUnitLimitInstruction(units: number): TransactionInstruction {
  const data = Buffer.alloc(5);
  data.writeUInt8(2, 0);
  data.writeUInt32LE(units, 1);
  return { programId: ComputeBudgetProgram.programId, keys: [], data };
}

function createComputeUnitPriceInstruction(microLamports: number): TransactionInstruction {
  const data = Buffer.alloc(9);
  data.writeUInt8(3, 0);
  data.writeBigUInt64LE(BigInt(microLamports), 1);
  return { programId: ComputeBudgetProgram.programId, keys: [], data };
}

export interface BuildInstruction {
  programId: PublicKey;
  keys: Array<{ pubkey: PublicKey; isSigner: boolean; isWritable: boolean }>;
  data: Buffer;
}

export interface BuildResult {
  instructions: BuildInstruction[];
  signers: PublicKey[];
  computeUnits?: number;
}

/**
 * Build the dummy `VersionedTransaction` used for priority-fee + simulation
 * pre-flight. Uses the all-1s blockhash so it's never a valid submission.
 */
export function buildDummyTransaction(
  buildResult: BuildResult,
  payer: PublicKey,
): VersionedTransaction {
  const message = MessageV0.compile({
    payerKey: payer,
    instructions: buildResult.instructions,
    recentBlockhash: '11111111111111111111111111111111',
  });
  return new VersionedTransaction(message);
}

export interface BuildFinalTxArgs {
  buildResult: BuildResult;
  priorityFee: number;
  blockhash: Pick<RecentBlockhash, 'blockhash'>;
  payer: PublicKey;
  /** Pass `options` + `jitoEnabled` + `network` to enable a Jito-tip
   *  instruction on the relay/external path. Omit for the Turnkey path
   *  (embedded wallets never tip Jito). */
  jito?: {
    options: SubmissionOptions;
    enabled: boolean;
    network: string;
  };
}

/**
 * Build the final, submission-ready `VersionedTransaction`. Strips any
 * caller-supplied ComputeBudget instructions (the contract-sdk prepends
 * them) and re-injects them with a unique nonce to bypass Solana's
 * dedup, then optionally appends a Jito tip.
 */
export function buildFinalTransaction(args: BuildFinalTxArgs): VersionedTransaction {
  const { buildResult, priorityFee, blockhash, payer, jito } = args;

  const nonce = nextNonce();
  const computeUnits = Math.min(
    (buildResult.computeUnits ?? DEFAULT_COMPUTE_UNITS) + nonce,
    MAX_COMPUTE_UNITS,
  );
  const COMPUTE_BUDGET_PROGRAM_ID = ComputeBudgetProgram.programId;
  const programInstructions = buildResult.instructions.filter(
    (ix) => !ix.programId.equals(COMPUTE_BUDGET_PROGRAM_ID),
  );
  const instructions: TransactionInstruction[] = [
    createComputeUnitLimitInstruction(computeUnits),
    createComputeUnitPriceInstruction(priorityFee),
    ...(programInstructions as TransactionInstruction[]),
  ];

  if (
    jito?.enabled &&
    isJitoAvailable(jito.network, jito.options.activeWallet.type) &&
    jito.options.jitoConfig?.tipLamports
  ) {
    const tipIx = createJitoTipInstruction(
      new PublicKey(jito.options.activeWallet.address),
      jito.options.jitoConfig.tipLamports,
    );
    instructions.push(tipIx);
  }

  const message = MessageV0.compile({
    payerKey: payer,
    instructions,
    recentBlockhash: blockhash.blockhash,
  });

  return new VersionedTransaction(message);
}
